// src/pages/usuarios/PerfilUsuario.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useEffect, useContext } from "react";
import { toast } from '../../utils/toast';
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Avatar,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Grid,
  Divider,
  TextField,
  Dialog,
  DialogContent,
  Stack,
  Paper,
  alpha,
} from "@mui/material";
import {
  AccountCircle, Security as SecurityIcon, ContactMail,
  PhotoCamera, Lock as LockIcon, Save, Cancel as CancelIcon,
  CheckCircle, Edit as EditIcon, Close as CloseIcon,
  Person, Badge as BadgeIcon, Business, CalendarToday,
  Email, Phone, Home, ArrowForward,
} from "@mui/icons-material";
import PageLayout from "../../components/layout/PageLayout";
import { DarkModeContext } from "../../context/darkModeContext";
import { useAuth } from "../../context/AuthContext";
import { PerfilApi } from "../../services/api/PerfilApi";
import { PerfilCompleto } from "../../components/types/types/perfil.types";
import Swal from "sweetalert2";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB", warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", errorBg: "#FBE4E4", purple: "#6940A5", purpleBg: "#EAE4F2", hover: "rgba(55,53,47,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A", warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", errorBg: "#3D2020", purple: "#9A6DD7", purpleBg: "#2A2040", hover: "rgba(255,255,255,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.2)" },
};

const InfoField: React.FC<{ icon: React.ReactElement; label: string; value: string; p: any }> = ({ icon, label, value, p }) => (
  <Box sx={{ p: 2, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg }}>
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.3 }}>
      <Box sx={{ color: p.textT, "& svg": { fontSize: 13 } }}>{icon}</Box>
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: "0.88rem", fontWeight: 500, color: p.text }}>{value || "—"}</Typography>
  </Box>
);

const PerfilUsuario: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const [tabValue, setTabValue] = useState(0);
  const [perfil, setPerfil] = useState<PerfilCompleto | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingContact, setEditingContact] = useState(false);
  const [openPwdDialog, setOpenPwdDialog] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [avatarKey, setAvatarKey] = useState(0);
  const [currentImage, setCurrentImage] = useState<string | undefined>();
  const [contactData, setContactData] = useState({ Email: "", Telefono: "", Direccion: "" });
  const [contactErrors, setContactErrors] = useState({ Email: "", Telefono: "" });
  const [pwdData, setPwdData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [pwdErrors, setPwdErrors] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });

  useEffect(() => {
    if (user?.Id !== Number(id)) { toast.error("Solo puedes ver tu propio perfil"); navigate("/"); return; }
    loadProfile();
  }, [id, user]);

  const loadProfile = async () => {
    try { setLoading(true); const data = await PerfilApi.getProfile(Number(id)); setPerfil(data); setCurrentImage(data.empleado?.Imagen || undefined); setContactData({ Email: data.empleado?.Email || "", Telefono: data.empleado?.Telefono || "", Direccion: data.empleado?.Direccion || "" }); }
    catch (e: any) { toast.error("Error"); }
    finally { setLoading(false); }
  };

  const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) { toast.warning("Archivo inválido"); return; }
    const reader = new FileReader();
    reader.onloadend = async () => {
      try { await PerfilApi.updatePhoto(Number(id), reader.result as string); await loadProfile(); setAvatarKey((k) => k + 1); toast.success("Foto actualizada"); }
      catch (e: any) { toast.error("Error"); }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveContact = async () => {
    if (!perfil?.empleado) return;
    const errors = { Email: contactData.Email && !PerfilApi.validateEmail(contactData.Email) ? "Email inválido" : "", Telefono: contactData.Telefono && !PerfilApi.validatePhone(contactData.Telefono) ? "Teléfono inválido" : "" };
    setContactErrors(errors); if (errors.Email || errors.Telefono) return;
    setSavingContact(true);
    try { await PerfilApi.updateContacto(perfil.empleado.Id, contactData); await loadProfile(); setEditingContact(false); toast.success("Actualizado"); }
    catch (e: any) { Swal.fire({ icon: "error", text: e.message }); }
    finally { setSavingContact(false); }
  };

  const handleChangePwd = async () => {
    const errors = { currentPassword: !pwdData.currentPassword ? "Requerida" : "", newPassword: "", confirmPassword: pwdData.newPassword !== pwdData.confirmPassword ? "No coinciden" : "" };
    const v = PerfilApi.validatePassword(pwdData.newPassword); if (!v.isValid) errors.newPassword = v.message;
    setPwdErrors(errors); if (Object.values(errors).some(Boolean)) return;
    setChangingPwd(true);
    try { await PerfilApi.changePassword(Number(id), pwdData.currentPassword, pwdData.newPassword); setOpenPwdDialog(false); setPwdData({ currentPassword: "", newPassword: "", confirmPassword: "" }); toast.success("Contraseña cambiada"); }
    catch (e: any) { Swal.fire({ icon: "error", text: e.message }); }
    finally { setChangingPwd(false); }
  };

  if (loading) return <PageLayout icon={AccountCircle} title="Mi Perfil"><Box display="flex" justifyContent="center" p={6}><CircularProgress size={32} sx={{ color: p.accent }} /></Box></PageLayout>;
  if (!perfil) return <PageLayout icon={AccountCircle} title="Mi Perfil"><Box sx={{ p: 3, borderRadius: "10px", bgcolor: p.errorBg, color: p.error }}>No se pudo cargar el perfil</Box></PageLayout>;

  const nombre = perfil.empleado ? `${perfil.empleado.Nombre} ${perfil.empleado.Apellidos}` : perfil.Nombre || perfil.Usuario;

  return (
    <PageLayout icon={AccountCircle} title="Mi Perfil">
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: "12px", border: `1px solid ${p.border}`, bgcolor: p.bg, boxShadow: p.shadow }}>
            <Stack direction="row" alignItems="center" spacing={3}>
              <Box sx={{ position: "relative" }}>
                <Avatar key={`av-${avatarKey}`} src={currentImage} sx={{ width: 72, height: 72, bgcolor: p.accentBg, color: p.accent, fontSize: "1.5rem", fontWeight: 700 }}>
                  {nombre.charAt(0).toUpperCase()}
                </Avatar>
                <input accept="image/*" style={{ display: "none" }} id="photo-upload" type="file" onChange={handlePhotoChange} />
                <label htmlFor="photo-upload">
                  <IconButton component="span" sx={{ position: "absolute", bottom: -4, right: -4, width: 28, height: 28, bgcolor: p.accent, color: "#FFF", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" }, "& svg": { fontSize: 14 } }}>
                    <PhotoCamera />
                  </IconButton>
                </label>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: p.text }}>{nombre}</Typography>
                <Typography sx={{ fontSize: "0.82rem", color: p.textS }}>{perfil.empleado?.cargo?.Nombre || "Sin cargo"}</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: p.textT, fontFamily: "'IBM Plex Mono', monospace" }}>@{perfil.Usuario}</Typography>
              </Box>
              <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                <Box sx={{ px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: p.purpleBg, color: p.purple, fontSize: "0.75rem", fontWeight: 600 }}>{perfil.rol.Nombre}</Box>
                <Box sx={{ px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: p.accentBg, color: p.accent, fontSize: "0.75rem", fontWeight: 600 }}>{perfil.perfil.Nombre}</Box>
              </Box>
            </Stack>
          </Paper>

          {/* Tabs */}
          <Box sx={{ mb: 3 }}>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} TabIndicatorProps={{ sx: { bgcolor: p.accent, height: 2, borderRadius: 1 } }}
              sx={{ "& .MuiTab-root": { textTransform: "none", fontSize: "0.84rem", fontWeight: 500, color: p.textT, "&.Mui-selected": { color: p.text, fontWeight: 600 } } }}>
              <Tab label="Información Personal" />
              <Tab label="Seguridad" />
              <Tab label="Contacto" />
            </Tabs>
          </Box>

          {/* Tab 0: Info Personal */}
          {tabValue === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><InfoField icon={<Person />} label="Usuario" value={perfil.Usuario} p={p} /></Grid>
              {perfil.empleado && <>
                <Grid item xs={12} md={6}><InfoField icon={<BadgeIcon />} label="Documento" value={`${perfil.empleado.tipoDocumento?.Nombre || ""} ${perfil.empleado.NumeroDocumento}`} p={p} /></Grid>
                <Grid item xs={12} md={6}><InfoField icon={<Business />} label="Cargo" value={perfil.empleado.cargo?.Nombre || "No asignado"} p={p} /></Grid>
                <Grid item xs={12} md={6}><InfoField icon={<Business />} label="Sucursal" value={perfil.empleado.sucursal?.Nombre || "No asignada"} p={p} /></Grid>
                {perfil.empleado.FechaNacimiento && <Grid item xs={12} md={6}><InfoField icon={<CalendarToday />} label="Nacimiento" value={new Date(perfil.empleado.FechaNacimiento).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })} p={p} /></Grid>}
                {perfil.empleado.FechaIngreso && <Grid item xs={12} md={6}><InfoField icon={<CalendarToday />} label="Ingreso" value={new Date(perfil.empleado.FechaIngreso).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })} p={p} /></Grid>}
              </>}
              <Grid item xs={12}>
                <Box sx={{ p: 2, borderRadius: "8px", bgcolor: p.accentBg, border: `1px solid ${alpha(p.accent, 0.2)}` }}>
                  <Typography sx={{ fontSize: "0.78rem", color: p.accent, fontWeight: 500 }}>Los datos personales son editados por el administrador</Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: Seguridad */}
          {tabValue === 1 && (
            <Box>
              <Button variant="contained" disableElevation onClick={() => setOpenPwdDialog(true)} startIcon={<LockIcon sx={{ fontSize: 16 }} />}
                sx={{ bgcolor: p.accent, color: "#FFF", mb: 3, fontSize: "0.84rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" } }}>
                Cambiar Contraseña
              </Button>
              <Grid container spacing={2}>
                {perfil.rol.rolesModulos?.filter((rm) => rm.Activo).sort((a, b) => a.modulo.Orden - b.modulo.Orden).map((rm) => (
                  <Grid item xs={12} sm={6} md={4} key={rm.Id}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckCircle sx={{ fontSize: 16, color: p.success }} />
                        <Box>
                          <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: p.text }}>{rm.modulo.Nombre}</Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: p.textT }}>{rm.modulo.Ruta}</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Tab 2: Contacto */}
          {tabValue === 2 && (
            <Box>
              {!editingContact ? (
                <>
                  <Button onClick={() => setEditingContact(true)} startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    sx={{ mb: 3, color: p.accent, fontSize: "0.84rem", fontWeight: 600, borderRadius: "8px", border: `1px solid ${p.border}`, "&:hover": { bgcolor: p.hover } }}>
                    Editar Contacto
                  </Button>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}><InfoField icon={<Email />} label="Email" value={perfil.empleado?.Email || ""} p={p} /></Grid>
                    <Grid item xs={12} md={6}><InfoField icon={<Phone />} label="Teléfono" value={perfil.empleado?.Telefono || ""} p={p} /></Grid>
                    <Grid item xs={12}><InfoField icon={<Home />} label="Dirección" value={perfil.empleado?.Direccion || ""} p={p} /></Grid>
                  </Grid>
                </>
              ) : (
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, mb: 0.5, textTransform: "uppercase" }}>Email</Typography>
                    <TextField size="small" fullWidth value={contactData.Email} onChange={(e) => setContactData({ ...contactData, Email: e.target.value })} error={!!contactErrors.Email} helperText={contactErrors.Email} sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bgS } }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, mb: 0.5, textTransform: "uppercase" }}>Teléfono</Typography>
                    <TextField size="small" fullWidth value={contactData.Telefono} onChange={(e) => setContactData({ ...contactData, Telefono: e.target.value })} error={!!contactErrors.Telefono} helperText={contactErrors.Telefono} sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bgS } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, mb: 0.5, textTransform: "uppercase" }}>Dirección</Typography>
                    <TextField size="small" fullWidth multiline rows={2} value={contactData.Direccion} onChange={(e) => setContactData({ ...contactData, Direccion: e.target.value })} sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bgS } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1.5}>
                      <Button variant="contained" disableElevation startIcon={<Save sx={{ fontSize: 16 }} />} onClick={handleSaveContact} disabled={savingContact}
                        sx={{ bgcolor: p.accent, color: "#FFF", fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px" }}>
                        {savingContact ? "Guardando..." : "Guardar"}
                      </Button>
                      <Button onClick={() => { setEditingContact(false); setContactData({ Email: perfil.empleado?.Email || "", Telefono: perfil.empleado?.Telefono || "", Direccion: perfil.empleado?.Direccion || "" }); }}
                        sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px" }}>Cancelar</Button>
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </Box>

        {/* Password Dialog */}
        <Dialog open={openPwdDialog} onClose={() => { setOpenPwdDialog(false); setPwdData({ currentPassword: "", newPassword: "", confirmPassword: "" }); setPwdErrors({ currentPassword: "", newPassword: "", confirmPassword: "" }); }} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg } }}>
          <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent }}><LockIcon sx={{ fontSize: 18 }} /></Box>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: p.text }}>Cambiar Contraseña</Typography>
            </Stack>
            <IconButton onClick={() => setOpenPwdDialog(false)} sx={{ width: 30, height: 30, borderRadius: "7px", color: p.textT }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
          </Box>
          <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
            <Stack spacing={2.5}>
              <Box sx={{ p: 2, borderRadius: "8px", bgcolor: p.accentBg, border: `1px solid ${alpha(p.accent, 0.2)}` }}>
                <Typography sx={{ fontSize: "0.75rem", color: p.accent, fontWeight: 500 }}>Mínimo 6 caracteres con letras y números</Typography>
              </Box>
              <TextField size="small" fullWidth type="password" label="Contraseña Actual" value={pwdData.currentPassword} onChange={(e) => setPwdData({ ...pwdData, currentPassword: e.target.value })} error={!!pwdErrors.currentPassword} helperText={pwdErrors.currentPassword} sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bg } }} />
              <TextField size="small" fullWidth type="password" label="Nueva Contraseña" value={pwdData.newPassword} onChange={(e) => setPwdData({ ...pwdData, newPassword: e.target.value })} error={!!pwdErrors.newPassword} helperText={pwdErrors.newPassword} sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bg } }} />
              <TextField size="small" fullWidth type="password" label="Confirmar Contraseña" value={pwdData.confirmPassword} onChange={(e) => setPwdData({ ...pwdData, confirmPassword: e.target.value })} error={!!pwdErrors.confirmPassword} helperText={pwdErrors.confirmPassword} sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bg } }} />
            </Stack>
          </DialogContent>
          <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${p.border}`, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button onClick={() => setOpenPwdDialog(false)} sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px" }}>Cancelar</Button>
            <Button variant="contained" disableElevation onClick={handleChangePwd} disabled={changingPwd || !pwdData.currentPassword || !pwdData.newPassword || !pwdData.confirmPassword}
              sx={{ bgcolor: p.accent, color: "#FFF", fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px" }}>
              {changingPwd ? "Cambiando..." : "Cambiar"}
            </Button>
          </Box>
        </Dialog>
      </PageLayout>
  );
};

export default PerfilUsuario;
